import { useEffect, useState } from 'react';
import { MIN_PASSWORD_LENGTH, type InvitePreview } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { Shell } from '../components/Shell';

interface Props {
  readonly mode: 'signin' | 'signup';
  /** Set when the visitor arrived on a share link and has no account yet. */
  readonly invite: string | null;
  readonly onSignedIn: (session: StoredSession) => void;
}

export const SignIn = ({ mode, invite, onSignedIn }: Props) => {
  const [signingUp, setSigningUp] = useState(mode === 'signup' || invite !== null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [deadInvite, setDeadInvite] = useState(false);

  // Say what the link is for before asking anybody to create an account for it.
  //
  // A link that no longer resolves is worth saying out loud rather than
  // silently falling back to a bare sign-up form: family links minted before
  // this deployment had accounts route here too, and their tokens are gone.
  useEffect(() => {
    if (!invite) return;
    let live = true;
    api
      .previewInvite(invite)
      .then((result) => {
        if (!live) return;
        setPreview(result);
        setDeadInvite(false);
      })
      .catch(() => live && setDeadInvite(true));
    return () => {
      live = false;
    };
  }, [invite]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = signingUp
        ? await api.signUp(email.trim(), password, name.trim())
        : await api.signIn(email.trim(), password);
      onSignedIn({ token: result.token, account: result.account });
      // Anyone who came in on an invite lands back on it, now signed in, and
      // the Join page finishes the job.
      if (!invite) go('/');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const ready =
    email.includes('@') &&
    password.length >= (signingUp ? MIN_PASSWORD_LENGTH : 1) &&
    (!signingUp || name.trim().length >= 2);

  return (
    <Shell>
      <div className="stack-loose" style={{ marginTop: '2.5rem' }}>
        <div>
          {preview && <p className="eyebrow">You have been invited</p>}
          <h1 className="title">
            {preview
              ? `${preview.invitedBy} shared ${preview.deviceLabel} with you`
              : signingUp
                ? 'Create your account'
                : 'Sign in'}
          </h1>
          <p className="lede">
            {preview
              ? preview.role === 'delegate'
                ? 'You will be able to help run the frame: settings, who else can send, and what goes on it.'
                : 'You will be able to send photos to the frame. Your name appears under each one.'
              : signingUp
                ? 'One account covers every frame you own and every one somebody shares with you.'
                : 'Photo frames, and the people you share them with.'}
          </p>
        </div>

        {deadInvite && (
          <p className="alert">
            That invite link has expired or been withdrawn — ask whoever sent it for a new one.
            You can still sign in or create an account here.
          </p>
        )}

        {error && <p className="alert">{error}</p>}

        <div className="stack">
          {signingUp && (
            <label className="field">
              <span>Your name</span>
              <input
                className="input"
                value={name}
                maxLength={40}
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
              />
            </label>
          )}

          <label className="field">
            <span>Email</span>
            <input
              className="input"
              type="email"
              value={email}
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>

          <label className="field">
            <span>Password</span>
            <input
              className="input"
              type="password"
              value={password}
              autoComplete={signingUp ? 'new-password' : 'current-password'}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>

          {signingUp && (
            <p className="note">
              At least {MIN_PASSWORD_LENGTH} characters. Three or four unrelated words beat
              anything with a punctuation mark bolted on the end.
            </p>
          )}

          <div className="row">
            <button
              type="button"
              className="button"
              disabled={busy || !ready}
              onClick={() => void submit()}
            >
              {busy ? 'Working' : signingUp ? 'Create account' : 'Sign in'}
            </button>
            <button
              type="button"
              className="button button-quiet"
              disabled={busy}
              onClick={() => {
                setSigningUp((current) => !current);
                setError(null);
              }}
            >
              {signingUp ? 'I already have one' : 'Create an account'}
            </button>
          </div>
        </div>
      </div>
    </Shell>
  );
};
